import { ReactElement, useMemo, useState } from 'react'
import AddUserInGroup from './AddUserInGroup'
import {
    Box,
    Button,
    Grid,
    IconButton,
    TextField,
    Typography,
    InputAdornment,
    Icon,
    LinearProgress,
} from '@mui/material'
import {
    MRT_ColumnDef,
    MRT_ColumnFiltersState,
    MRT_RowSelectionState,
    MRT_SortingState,
} from 'material-react-table'
import { useInfiniteQuery } from '@tanstack/react-query'
import MRTTable from '../Common/Table'
import SearchIcon from '@mui/icons-material/Search'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppSelector } from '../../redux/hook'
import theme from '../../theme/theme'
import { ArrowBackOutlined } from '@mui/icons-material'
import {
    deleteUserInGroup,
    fetchUsersByGruopID,
} from '../../services/groupsServices'
import {
    IformData,
    UserInGroup,
    IOpenAddOrEditUser,
    UserInGroupApiResponse,
} from './interfaces'
import Subscriptions from '../Subscriptions/Subscriptions'
import { useTranslation } from 'react-i18next'
import AccessControl from './AccessControl'
import { RoutePaths, UserPermissions } from '../../constants/constants'
import { usePermissions } from '../../hooks/usePermissions'

const defaultFormData: IformData = {
    role: '',
    email: '',
}

const fetchSize = 10
const actionColumnSize = 20

const actionCellSx = {
    backgroundColor: '#E9F6FF',
    borderBottom: '3px solid #FFFFFF',
    borderTop: '3px solid #FFFFFF',
    fontSize: '14px',
    padding: '0px',
    textAlign: 'center',
}

const displayColumnDefOptions = {
    'mrt-row-select': {
        header: '',
        size: actionColumnSize,
        minSize: actionColumnSize,
        grow: false,
        maxSize: actionColumnSize,
        muiTableBodyCellProps: {
            sx: {
                ...actionCellSx,
                '& .MuiCheckbox-root': {
                    padding: '2px',
                },
            },
        },
        muiTableHeadCellProps: {
            sx: {
                padding: '0px',
                textAlign: 'center',
            },
        },
    },
}

const UsersListInGroup = (): ReactElement => {
    const { t } = useTranslation()
    const { hasPermission } = usePermissions()
    const hasWriteAccess = hasPermission(UserPermissions.USER_ADMIN)
    const columns: MRT_ColumnDef<UserInGroup>[] = [
        {
            accessorKey: 'email',
            header: t('userEmail'),
            muiTableBodyCellProps: {
                role: 'gridcell',
            },
        },
        {
            accessorKey: 'name',
            header: t('invitedBy'),
            muiTableBodyCellProps: {
                role: 'gridcell', // explicitly set or leave MRT to handle it
            },
        },
    ]
    const { groupName, groupId } = useParams()
    const [tableKey, setTableKey] = useState(0)
    const [openSubscriptions, setOpenSubscriptions] = useState(false)
    const [openAddOrEditUser, setOpenAddOrEditUser] =
        useState<IOpenAddOrEditUser>({
            mode: 'Add',
            open: false,
            formData: defaultFormData,
        })
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
        [],
    )
    const [globalFilter, setGlobalFilter] = useState<string>()
    const [sorting, setSorting] = useState<MRT_SortingState>([])

    //access control
    const [isOpenAccessControl, setIsOpenAccessControl] =
        useState<boolean>(false)

    const navigate = useNavigate()

    const orgSlice = useAppSelector((state) => state.org)
    const { org } = orgSlice

    const handleAddOrEditUser = (refreshTable: boolean) => {
        if (refreshTable) {
            setTableKey((prevKey) => prevKey + 1)
            setRowSelection({})
        }
        setOpenAddOrEditUser({
            mode: 'Add',
            open: false,
            formData: defaultFormData,
        })
    }

    const handleOpen = (mode: string, data: IformData) => {
        setOpenAddOrEditUser({
            mode,
            open: true,
            formData: data,
        })
    }

    const handleCloseSubscriptions = () => {
        setOpenSubscriptions(false)
    }

    const { data, fetchNextPage, isError, isFetching, isLoading } =
        useInfiniteQuery<UserInGroupApiResponse>({
            queryKey: [
                'table-data',
                columnFilters,
                globalFilter,
                sorting,
                tableKey,
                groupId,
            ],
            queryFn: async ({ pageParam }) => {
                let filterBy = sorting[0]?.id
                const sortOrder = filterBy
                    ? sorting[0]?.desc
                        ? 'DESC'
                        : 'ASC'
                    : ''
                if (filterBy === 'Created At') {
                    filterBy = 'CREATED'
                }
                if (org!.id) {
                    const response = await fetchUsersByGruopID(
                        {
                            pageNumber: (pageParam as number) + 1,
                            size: fetchSize,
                            searchText: globalFilter,
                            filterBy: filterBy ? filterBy.toUpperCase() : '',
                            sortOrder,
                            orgId: org!.id,
                        },
                        groupId,
                    )
                    return response as UserInGroupApiResponse
                } else {
                    return {} as UserInGroupApiResponse
                }
            },
            initialPageParam: 0,
            getNextPageParam: (_lastGroup, groups) => groups.length,
            refetchOnWindowFocus: false,
        })

    const flatData = useMemo(
        () => data?.pages.flatMap((page) => page?.users) ?? [],
        [data],
    )

    const totalDBRowCount = data?.pages?.[0]?.meta?.totalRowCount ?? 0

    const openAddUser = () => {
        handleOpen('Add', { ...defaultFormData, totalUsers: totalDBRowCount })
    }

    const deleteUserHandler = async () => {
        const keys = Object.keys(rowSelection)
        const usersToDelete: number[] = keys.map(
            (key) => flatData[Number(key)].id,
        )
        const deleteReqBody = {
            userId: usersToDelete,
            groupId,
        }
        await deleteUserInGroup(deleteReqBody, org!.id)

        setRowSelection({})
        setTableKey((prevKey) => prevKey + 1)
    }

    const onBackToUserGrops = () => {
        navigate(RoutePaths.userGroups)
    }

    const disableDeleteButton = Object.keys(rowSelection).length < 1

    if (!org) {
        return <LinearProgress />
    }

    return (
        <Grid
            sx={{
                margin: '30px',
                border: `1px solid ${theme.palette.primary.main}`,
                borderRadius: '20px',
                maxHeight: `calc(100vh - 199px)`,
                overflow: 'auto',
            }}
        >
            <Grid
                container
                sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 3,
                }}
            >
                <Grid>
                    <Box display="flex" alignItems="center">
                        <Icon
                            onClick={() => onBackToUserGrops()}
                            sx={{ marginRight: '15px', cursor: 'pointer' }}
                            fontSize="medium"
                            id="dackToUserGrops-button"
                        >
                            <ArrowBackOutlined />
                        </Icon>
                        <Typography variant="h4" component="h2">
                            {t('userGroupsBreadcrumb', { groupName })}
                        </Typography>
                    </Box>
                </Grid>
                <Grid
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <Button
                        onClick={() => setIsOpenAccessControl(true)}
                        variant="contained"
                        color="primary"
                        sx={{
                            textTransform: 'none',
                            borderRadius: '20px',
                            padding: '6px 16px',
                            fontWeight: 'bold',
                            height: '36px',
                            marginRight: '10px',
                        }}
                        id="shipment-access-button"
                    >
                        {t('Access Control')}
                    </Button>
                    <Button
                        onClick={() => setOpenSubscriptions(true)}
                        variant="contained"
                        color="primary"
                        sx={{
                            textTransform: 'none',
                            borderRadius: '20px',
                            padding: '6px 16px',
                            fontWeight: 'bold',
                            height: '36px',
                            marginRight: '10px',
                        }}
                        id="subscriptions-button"
                    >
                        {t('subscriptions')}
                    </Button>

                    <Box
                        sx={{
                            background: '#E3F4FF',
                            borderRadius: '25px',
                            marginRight: '10px',
                        }}
                    >
                        <TextField
                            id="users-in-group-list-search"
                            sx={{
                                padding: 0,
                                '& fieldset': { border: 'none' },
                            }}
                            placeholder={t('search')}
                            role="search"
                            value={globalFilter}
                            onChange={(e) => setGlobalFilter(e.target.value)}
                            className="table-search-bar"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconButton
                                            sx={{
                                                '&:hover ': {
                                                    background: 'none',
                                                    cursor: 'auto',
                                                },
                                            }}
                                        >
                                            <SearchIcon
                                                sx={{ color: 'primary' }}
                                            />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                style: {
                                    height: 36,
                                    paddingLeft: '5px',
                                },
                            }}
                        />
                    </Box>
                    {hasWriteAccess ? (
                        <>
                            <Box
                                sx={{
                                    cursor: disableDeleteButton
                                        ? 'not-allowed'
                                        : 'default',
                                }}
                            >
                                <Button
                                    onClick={deleteUserHandler}
                                    disabled={disableDeleteButton}
                                    sx={{
                                        minWidth: '25px',
                                        maxWidth: '30px',
                                        marginLeft: '5px',
                                        filter: disableDeleteButton
                                            ? 'grayscale(100%)'
                                            : 'none',
                                        opacity: disableDeleteButton ? 0.5 : 1,
                                    }}
                                    id="delete-userInGroup-button"
                                >
                                    <img
                                        src={`/assets/images/icons/delete-icon.svg`}
                                        alt={t('deleteUserFromGroup')}
                                    />
                                </Button>
                            </Box>
                            <Button
                                onClick={openAddUser}
                                sx={{
                                    minWidth: '25px',
                                    maxWidth: '30px',
                                    marginLeft: '15px',
                                    minHeight: '25px',
                                    maxHeight: '30px',
                                    marginRight: '10px',
                                }}
                                id="add-userInGroup-button"
                            >
                                <img
                                    src={`/assets/images/icons/add-icon.svg`}
                                    alt={t('addUserToGroup')}
                                />
                            </Button>
                        </>
                    ) : null}
                    {openAddOrEditUser.open && (
                        <AddUserInGroup
                            openAddOrEditUser={openAddOrEditUser}
                            org={org}
                            handleAddOrEditUser={handleAddOrEditUser}
                            groupId={groupId}
                        />
                    )}
                </Grid>
            </Grid>
            <MRTTable<UserInGroup, UserInGroupApiResponse>
                columns={columns}
                sorting={sorting}
                globalFilter={globalFilter}
                columnFilters={columnFilters}
                setRowSelection={setRowSelection}
                rowSelection={rowSelection}
                setColumnFilters={setColumnFilters}
                setGlobalFilter={setGlobalFilter}
                setSorting={setSorting}
                data={flatData}
                totalDBRowCount={totalDBRowCount}
                fetchNextPage={fetchNextPage}
                isError={isError}
                isFetching={isFetching}
                isLoading={isLoading}
                enableBottomToolbar={true}
                isCurvedBottomToolbar={true}
                enableRowSelection={hasWriteAccess}
                displayColumnDefOptions={displayColumnDefOptions}
                maxHeight="calc(100vh - 348px)"
            />

            {openSubscriptions && (
                <Subscriptions
                    openSubscriptions={openSubscriptions}
                    userGroupId={groupId || ''}
                    handleCloseSubscriptions={handleCloseSubscriptions}
                    readOnly={!hasWriteAccess}
                />
            )}
            {isOpenAccessControl && (
                <AccessControl
                    userGroupId={groupId || ''}
                    handleClose={() => setIsOpenAccessControl(false)}
                    readOnly={!hasWriteAccess}
                />
            )}
        </Grid>
    )
}

export default UsersListInGroup
